import React, { useEffect } from 'react'
import Modal from '../../../../ui_reusable/Modal'
import { useRegionsPartialUpdate } from '../../../../../api/generated/endpoints/regions/regions'
import type { RegionDataset } from '../../../../../api/generated/models'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Message, Form } from 'rsuite'
import { useState } from 'react'
import useStandardToaster from '../../../../ui_reusable/StandardToaster'

interface EditRegionDatasetProps {
  regionDataset: RegionDataset
  open: boolean
  onClose: () => void
}

const EditRegionDataset: React.FC<EditRegionDatasetProps> = ({
  regionDataset,
  open,
  onClose,
}) => {
  const { pushToast } = useStandardToaster()
  const queryClient = useQueryClient()
  const [formValue, setFormValue] = useState({
    name: regionDataset.region?.name,
    population: regionDataset.region?.population,
  })
  const [formError, setFormError] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormValue({
        name: regionDataset.region?.name,
        population: regionDataset.region?.population,
      })
      setFormError({})
    }
    // eslint-disable-next-line
  }, [open, regionDataset.region?.name])

  const updateMutation = useRegionsPartialUpdate({
    mutation: {
      onSuccess: () => {
        // Invalidate driver list queries to refresh the databook
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/with-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/without-channels'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/v2/datasets/drivers/'],
        })
        queryClient.invalidateQueries({
          queryKey: ['/api/datasets/region-datasets/'],
        })

        pushToast(
          <Message type="success">Region updated successfully!</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
        onClose()
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.detail ||
          error?.message ||
          'Failed to update region'

        pushToast(<Message type="error">{errorMessage}</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
      },
    },
  })

  const handleSubmit = async () => {
    // Validate form
    const errors: Record<string, string> = {}

    if (!formValue.name || formValue.name.trim() === '') {
      errors.name = 'Region name is required'
    }

    if (Object.keys(errors).length > 0) {
      setFormError(errors)
      return
    }

    try {
      await updateMutation.mutateAsync({
        id: String(regionDataset.id),
        data: {
          name: formValue.name?.trim(),
          population: formValue.population,
          // @ts-ignore
          client_id: regionDataset._parentDataset.client.id,
        },
      })
    } catch (error: any) {
      // Error handling is done in onError callback
      console.error('Update regigon error:', error)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${regionDataset.region?.name}`}
    >
      <Modal.Body>
        <Form
          fluid
          formValue={formValue}
          onChange={(value) =>
            setFormValue(value as { name: string; population: number })
          }
          formError={formError}
        >
          <Form.Group controlId="name">
            <Form.ControlLabel>Region Name</Form.ControlLabel>
            <Form.Control
              name="name"
              autoComplete="off"
              errorMessage={formError.name}
              errorPlacement="bottomStart"
            />
          </Form.Group>
          <Form.Group controlId="population">
            <Form.ControlLabel>Population</Form.ControlLabel>
            <Form.Control
              name="population"
              autoComplete="off"
              type="number"
              errorMessage={formError.population}
              errorPlacement="bottomStart"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          onClick={handleSubmit}
          appearance="primary"
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Updating...' : 'Update Region'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default EditRegionDataset
